/**
 * SignalR Context
 * Provides SignalR connection and notification handling throughout the app
 */
import { createContext, useEffect, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { signalRService } from '@/services/signalr.service';
import { useAuthState } from '@/hooks/auth/useAuth';
import {
  type SignalRNotification,
  type ProgressNotificationDto,
  type ProgressUpdateDto,
  NotificationSeverity,
} from '@/types/signalr-notifications';
import { useNotification } from '@/hooks/useNotification';
import { unifiedNotificationKeys, commentKeys, dashboardStatsKeys, taxBinderKeys } from '@/lib/query/options';
import { createLogger } from '@/utils/logger';
import { NotificationActionType } from '@ata-digital/client-tax-system-api-client';

const log = createLogger('SignalRContext');

interface SignalRContextType {
  connectionState: HubConnectionState | null;
  isConnected: boolean;
  notifications: SignalRNotification[];
  activeOperations: Map<string, ProgressNotificationDto>;
  clearNotifications: () => void;
}

export const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

interface SignalRProviderProps {
  children: ReactNode;
}

/**
 * Convert raw progress update to full progress notification
 * Uses explicit operationType from backend (no text parsing needed!)
 */
function convertProgressUpdate(update: ProgressUpdateDto): ProgressNotificationDto {
  const isCompleted = update.progress >= 100;

  return {
    type: null,
    operationId: update.operationId,
    operationType: update.operationType, // ✅ Using explicit field from backend
    progress: update.progress,
    currentStep: update.currentStep,
    currentStepNumber: update.currentStepNumber,
    totalSteps: update.totalSteps,
    isCompleted,
    hasError: false,
    title: 'Operation in progress',
    message: update.currentStep,
    severity: isCompleted ? NotificationSeverity.Success : NotificationSeverity.Info,
    timestamp: update.timestamp,
  };
}

export function SignalRProvider({ children }: SignalRProviderProps) {
  const { data: authState } = useAuthState();
  const { showSuccess, showError, showInfo, showWarning } = useNotification();
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] = useState<HubConnectionState | null>(null);
  const [notifications, setNotifications] = useState<SignalRNotification[]>([]);
  const [activeOperations, setActiveOperations] = useState<Map<string, ProgressNotificationDto>>(new Map());

  // Connect to SignalR when authenticated
  useEffect(() => {
    log.debug('Auth state changed', {
      isAuthenticated: authState?.isAuthenticated,
      hasToken: !!authState?.token,
    });

    if (authState?.isAuthenticated && authState?.token) {
      log.debug('Attempting to connect...');
      signalRService.connect(authState.token);

      // Set up periodic connection state check
      const interval = setInterval(() => {
        const state = signalRService.getConnectionState();
        setConnectionState(state);
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    } else {
      // Disconnect when not authenticated
      log.debug('Not authenticated, disconnecting...');
      signalRService.disconnect();
      setConnectionState(null);
    }
  }, [authState?.isAuthenticated, authState?.token]);

  // Listen for token refresh events from tokenManager and SignalR auth errors
  useEffect(() => {
    /**
     * Handle token refresh by reconnecting SignalR with the new token
     * This is triggered when tokenManager successfully refreshes the token
     */
    const handleTokenRefresh = async () => {
      log.debug('Token refresh detected, checking for new token...');

      // Get the latest auth state from the query cache
      // Note: We can't rely on authState here because it may not have updated yet
      const token = authState?.token;

      if (token && authState?.isAuthenticated) {
        log.debug('Reconnecting with refreshed token...');
        try {
          await signalRService.updateToken(token);
          log.debug('Successfully reconnected with new token');
        } catch (error) {
          log.error('Failed to reconnect with new token:', error);
        }
      }
    };

    /**
     * Handle token expiration by disconnecting SignalR
     * This is triggered when the refresh token is invalid or expired
     */
    const handleTokenExpired = () => {
      log.debug('Token expired, disconnecting SignalR...');
      signalRService.disconnect();
      setConnectionState(null);
    };

    /**
     * Handle SignalR-specific token expiration during reconnection
     * This is triggered when SignalR's reconnection fails with 401
     * We need to get a fresh token and reconnect
     */
    const handleSignalRTokenExpired = async () => {
      log.debug('SignalR token expired during reconnection, attempting to reconnect with current token...');

      // First disconnect the stale connection
      await signalRService.disconnect();
      setConnectionState(null);

      // Get the current token from auth state
      const token = authState?.token;

      if (token && authState?.isAuthenticated) {
        log.debug('Reconnecting SignalR with current auth token...');
        try {
          // Small delay to ensure clean disconnect
          await new Promise((resolve) => setTimeout(resolve, 100));
          await signalRService.connect(token);
          log.debug('Successfully reconnected SignalR with current token');
        } catch (error) {
          log.error('Failed to reconnect SignalR:', error);
        }
      } else {
        log.warn('Cannot reconnect SignalR - no valid auth token available');
      }
    };

    // Listen for custom events dispatched by tokenManager
    window.addEventListener('auth:token-refreshed', handleTokenRefresh);
    window.addEventListener('auth:token-expired', handleTokenExpired);
    // Listen for SignalR-specific token expiration (401 during reconnect)
    window.addEventListener('signalr:token-expired', handleSignalRTokenExpired);

    return () => {
      window.removeEventListener('auth:token-refreshed', handleTokenRefresh);
      window.removeEventListener('auth:token-expired', handleTokenExpired);
      window.removeEventListener('signalr:token-expired', handleSignalRTokenExpired);
    };
  }, [authState?.token, authState?.isAuthenticated]);

  const handleProgressNotification = useCallback(
    (progress: ProgressNotificationDto) => {
      // Always update the operation in the map (don't remove immediately on completion)
      setActiveOperations((prev) => {
        const newMap = new Map(prev);
        newMap.set(progress.operationId, progress);

        if (progress.isCompleted) {
          // Auto-cleanup completed operations after 5 seconds
          setTimeout(() => {
            setActiveOperations((current) => {
              const updated = new Map(current);
              if (updated.get(progress.operationId)?.isCompleted) {
                updated.delete(progress.operationId);
              }
              return updated;
            });
          }, 5000);
        }

        return newMap;
      });

      // Show completion toast
      if (progress.isCompleted) {
        if (progress.hasError) {
          showError(progress.errorMessage || 'Operation failed');
        } else {
          showSuccess(progress.message);
        }
      }
    },
    [showSuccess, showError]
  );

  const showToastNotification = useCallback(
    (notification: SignalRNotification) => {
      const toastFunctions = {
        [NotificationSeverity.Info]: showInfo,
        [NotificationSeverity.Success]: showSuccess,
        [NotificationSeverity.Warning]: showWarning,
        [NotificationSeverity.Error]: showError,
      };

      const toastFn = toastFunctions[notification.severity] || showInfo;

      toastFn(notification.message, {
        action: notification.requiresAction
          ? {
              label: notification.actionText || 'View',
              onClick: () => {
                if (notification.actionUrl) {
                  window.location.href = notification.actionUrl;
                }
              },
            }
          : undefined,
      });
    },
    [showInfo, showSuccess, showWarning, showError]
  );

  const handleNotification = useCallback(
    (notification: SignalRNotification) => {
      // Detect progress notifications (even if type field is missing/incorrect)
      // Backend may send progress data without proper type field
      const looksLikeProgress =
        'operationId' in notification &&
        'operationType' in notification &&
        'progress' in notification &&
        'currentStep' in notification;

      if (looksLikeProgress) {
        // Normalize severity case (backend sends lowercase, frontend expects capitalized)
        const normalizeSeverity = (severity?: string): NotificationSeverity => {
          if (!severity) return NotificationSeverity.Info;
          const lowerSeverity = severity.toLowerCase();
          if (lowerSeverity === 'success') return NotificationSeverity.Success;
          if (lowerSeverity === 'warning') return NotificationSeverity.Warning;
          if (lowerSeverity === 'error') return NotificationSeverity.Error;
          return NotificationSeverity.Info;
        };

        // Cast to progress notification with proper type checking
        const rawProgress = notification as Partial<ProgressNotificationDto>;

        // Ensure it has the type field for consistency
        const progressNotification: ProgressNotificationDto = {
          ...rawProgress,
          operationId: rawProgress.operationId || '',
          operationType: rawProgress.operationType || '',
          progress: rawProgress.progress ?? 0,
          currentStep: rawProgress.currentStep || '',
          type: null,
          title: notification.title || 'Operation in progress',
          message: notification.message || rawProgress.currentStep || 'Processing...',
          severity: normalizeSeverity(notification.severity as string),
          timestamp: notification.timestamp || new Date().toISOString(),
          isCompleted: rawProgress.isCompleted ?? (rawProgress.progress ?? 0) >= 100,
          hasError: rawProgress.hasError ?? false,
          requiresAction: notification.requiresAction ?? false,
        };

        handleProgressNotification(progressNotification);
      }
      // Handle other notification types (user notifications)
      else {
        // Add to notification list
        setNotifications((prev) => [notification, ...prev].slice(0, 50)); // Keep last 50

        // Show toast notification
        showToastNotification(notification);

        // Invalidate notification queries to refresh unread count and notification lists
        // This ensures the bell badge updates immediately when a new notification arrives
        queryClient.invalidateQueries({ queryKey: unifiedNotificationKeys.all });

        // Invalidate comment-related caches when a comment notification arrives
        // This updates: grouped comments, unread message badges, dashboard stats, binder list badges
        // Matches the invalidation pattern in useMarkCommentAsRead (src/hooks/api/useComments.ts)
        if (notification.type === NotificationActionType.CommentCreated) {
          log.debug('Comment notification detected, invalidating comment caches', notification);
          queryClient.invalidateQueries({ queryKey: commentKeys.all });
          queryClient.invalidateQueries({ queryKey: dashboardStatsKeys.stats() });
          queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
        }
      }
    },
    [handleProgressNotification, showToastNotification, queryClient]
  );

  // Use refs for handlers to avoid re-subscribing on every callback change
  const handleNotificationRef = useRef(handleNotification);
  handleNotificationRef.current = handleNotification;
  const handleProgressNotificationRef = useRef(handleProgressNotification);
  handleProgressNotificationRef.current = handleProgressNotification;

  // Handle notifications (subscribe once, ref ensures latest handler is used)
  useEffect(() => {
    const unsubscribe = signalRService.onNotification((notification: SignalRNotification) => {
      handleNotificationRef.current(notification);
    });

    return unsubscribe;
  }, []);

  // Handle progress updates (new backend ReceiveProgressUpdate events)
  useEffect(() => {
    const handleProgressUpdate = (update: ProgressUpdateDto) => {
      // Convert to ProgressNotificationDto format
      const progressNotification = convertProgressUpdate(update);

      // Handle as normal progress notification
      handleProgressNotificationRef.current(progressNotification);
    };

    const unsubscribe = signalRService.onProgressUpdate(handleProgressUpdate);

    return unsubscribe;
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const isConnected = connectionState === HubConnectionState.Connected;

  // Memoize context value to prevent unnecessary re-renders of consumers
  // Without this, every state change creates a new object reference,
  // causing all useSignalR() consumers to re-render
  const contextValue = useMemo(
    () => ({
      connectionState,
      isConnected,
      notifications,
      activeOperations,
      clearNotifications,
    }),
    [connectionState, isConnected, notifications, activeOperations, clearNotifications]
  );

  return <SignalRContext.Provider value={contextValue}>{children}</SignalRContext.Provider>;
}

export { useSignalR } from '@/hooks/useSignalR';
